import React, { FC, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Flex } from '../../../Flex/flex.component';
import { Button } from '../../../Form/Button/button.scomponent';
import { CubageVariantFormDrawer } from '../../../Drawer/variants/cubage-variant-form/cuabge-variant-form-drawer.component';
import { Placeholder } from '../../../Placeholder/placeholder.component';
import { IOptionsProps } from '../../../Form/Select/select.component';
import { ICubageVariantEntity } from '../../../../Services/api/cubages-variants/cubages-variants.interface';
import { ActionButtons } from '../../../Form/ActionButtons/action-buttons.component';
import { ConfirmDialog } from '../../../Dialog/variants/dialog-confirmation/dialog-confirmation.component';

interface ICubageTableProps {
  cubageProductIdSelected?: string;
  boxOptions: IOptionsProps[];
  handleAction: (data?: any) => Promise<void>;
  setCubageProductIdSelected: React.Dispatch<React.SetStateAction<string>>;
  data: {
    id: string;
    name: string;
    variants: ICubageVariantEntity[];
  }[];
  setCubageVariantSelected: (state: ICubageVariantEntity | undefined) => void;
  cubageVariantSelected: ICubageVariantEntity | undefined;
  cubageVariantToDestroy: string | undefined;
  setCubageVariantToDestroy: (id: string) => void;
  handleDestroyVariant: (id: string) => void;
  handleShowCubage: (id: string) => void;
  setVariantProductToDestroy: (data: string) => void;
}

const formatBoxLabel = (variant: ICubageVariantEntity) => {
  const box = variant.box;
  if (!box) return '—';
  return `${box.code} — ${box.length}×${box.width}×${box.height} cm · ${variant.boxWeight} kg`;
};

const headerCellSx = {
  fontWeight: 600,
  fontSize: '0.8125rem',
  bgcolor: 'background.paper',
  whiteSpace: 'nowrap',
} as const;

interface CubageRowData {
  name: string;
  cubageProductId: string;
  variants: ICubageVariantEntity[];
}

function Row(props: {
  row: CubageRowData;
  setCubageProductIdSelected: React.Dispatch<React.SetStateAction<string>>;
  setShowNewVariant: React.Dispatch<React.SetStateAction<boolean>>;
  setCubageVariantSelected: (state: ICubageVariantEntity | undefined) => void;
  setCubageVariantToDestroy: (id: string) => void;
  setVariantProductToDestroy: (data: string) => void;
  handleShowCubage: (id: string) => void;
}) {
  const {
    row,
    setCubageProductIdSelected,
    setShowNewVariant,
    setCubageVariantSelected,
    setCubageVariantToDestroy,
    setVariantProductToDestroy,
    handleShowCubage,
  } = props;
  const [open, setOpen] = useState(false);
  const variantCount = row.variants?.length ?? 0;

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell padding="checkbox" sx={{ width: 48 }}>
          <IconButton
            aria-label={open ? 'Recolher cubagens' : 'Expandir cubagens'}
            size="small"
            onClick={() => setOpen((prev) => !prev)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.4 }}>
            {row.name}
          </Typography>
        </TableCell>
        <TableCell align="center" sx={{ width: 120 }}>
          <Chip
            label={variantCount === 1 ? '1 cubagem' : `${variantCount} cubagens`}
            size="small"
            color={variantCount > 0 ? 'primary' : 'default'}
            variant={variantCount > 0 ? 'filled' : 'outlined'}
          />
        </TableCell>
        <TableCell align="right" sx={{ width: 80 }}>
          <ActionButtons
            editFunction={() => handleShowCubage(row.cubageProductId)}
            setItemToDestroy={setVariantProductToDestroy}
            params={{ row: { id: row.cubageProductId } }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, borderBottom: open ? 1 : 0, borderColor: 'divider' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: { xs: 0, sm: 1 } }}>
              <Flex
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 2 }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Variações de cubagem
                </Typography>
                <Button
                  variant="outlined"
                  sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 180 }, flexShrink: 0 }}
                  onClick={() => {
                    setShowNewVariant(true);
                    setCubageProductIdSelected(row.cubageProductId);
                  }}
                >
                  Adicionar cubagem
                </Button>
              </Flex>

              {variantCount > 0 ? (
                <TableContainer
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Descrição / Tamanho</TableCell>
                        <TableCell sx={headerCellSx} align="right">Qtde na caixa</TableCell>
                        <TableCell sx={headerCellSx} align="right">Pacotes</TableCell>
                        <TableCell sx={headerCellSx} align="right">Por pacote</TableCell>
                        <TableCell sx={{ ...headerCellSx, minWidth: 200 }}>Caixa</TableCell>
                        <TableCell sx={{ ...headerCellSx, width: 80 }} align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {row.variants.map((variant) => (
                        <TableRow key={variant.id} hover>
                          <TableCell>
                            <Typography variant="body2">{variant.description || '—'}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {variant.howManyInBox ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {variant.howManyPackages ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {variant.howManyPerPackage ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal', lineHeight: 1.35 }}>
                              {formatBoxLabel(variant)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <ActionButtons
                              params={{ row: { ...variant, id: variant.id } }}
                              setItemToDestroy={setCubageVariantToDestroy}
                              editFunction={setCubageVariantSelected}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma cubagem cadastrada para este produto.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const CubageTable: FC<ICubageTableProps> = ({
  data,
  handleAction,
  cubageProductIdSelected,
  boxOptions,
  setCubageProductIdSelected,
  setCubageVariantSelected,
  cubageVariantSelected,
  cubageVariantToDestroy,
  setCubageVariantToDestroy,
  handleDestroyVariant,
  handleShowCubage,
  setVariantProductToDestroy,
}) => {
  const [showNewVariant, setShowNewVariant] = useState(false);

  const rows: CubageRowData[] = data?.map((item) => ({
    name: item.name,
    cubageProductId: item.id,
    variants: item.variants ?? [],
  })) ?? [];

  return (
    <>
      <ConfirmDialog
        title="Remover variação de cubagem"
        onCancel={() => setCubageVariantToDestroy('')}
        description="Você tem certeza?"
        open={!!cubageVariantToDestroy}
        onConfirm={() => handleDestroyVariant(String(cubageVariantToDestroy))}
      />

      {(showNewVariant || !!cubageVariantSelected?.id) && (
        <CubageVariantFormDrawer
          open={showNewVariant || !!cubageVariantSelected?.id}
          handleSelect={() => {}}
          onToggle={() => {
            if (cubageVariantSelected?.id) {
              setCubageVariantSelected(undefined);
            } else {
              setShowNewVariant((prev) => !prev);
            }
          }}
          title={cubageVariantSelected?.id ? 'Edição de cubagem' : 'Nova cubagem'}
          subTitle="Preencha os dados abaixo"
          handleAction={handleAction}
          cubageProductId={cubageProductIdSelected || cubageVariantSelected?.id}
          boxOptions={boxOptions}
          cbFunction={() => {
            setShowNewVariant(false);
            setCubageProductIdSelected('');
            setCubageVariantSelected(undefined);
          }}
          cubageVariantSelected={cubageVariantSelected}
        />
      )}

      {rows.length > 0 ? (
        <TableContainer
          sx={{
            width: '100%',
            maxHeight: 'calc(100vh - 280px)',
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Table stickyHeader aria-label="Tabela de cubagem">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellSx, width: 48 }} />
                <TableCell sx={headerCellSx}>Produto</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 120 }} align="center">
                  Cubagens
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: 80 }} align="right">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <Row
                  key={row.cubageProductId}
                  row={row}
                  setCubageProductIdSelected={setCubageProductIdSelected}
                  setShowNewVariant={setShowNewVariant}
                  setCubageVariantSelected={setCubageVariantSelected}
                  setCubageVariantToDestroy={setCubageVariantToDestroy}
                  setVariantProductToDestroy={setVariantProductToDestroy}
                  handleShowCubage={handleShowCubage}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Placeholder description="Nenhum produto de cubagem encontrado." />
      )}
    </>
  );
};

export { CubageTable };
